/**
 * KPI Performance Store
 *
 * Zustand store for the KPI module: cycles, definitions, assignments,
 * dashboards, AI chat, and active views.
 */
import { create } from 'zustand';
import type {
  PerformanceCycle, KPIDefinition, KPIAssignment, KPICheckIn,
  KPIComment, KPIReview, DashboardMyKPIs, DashboardTeam, DashboardOrg,
} from '../services/api/kpi';
import * as kpiApi from '../services/api/kpi';

const _kpiInflight: Record<string, Promise<void>> = {};

type KPIView = 'my-kpis' | 'team' | 'organization' | 'catalog' | 'cycles';

interface KPIState {
  // Navigation
  activeView: KPIView;
  selectedAssignmentId: string | null;
  showAssignModal: boolean;
  showCheckInForm: boolean;

  // Cycles
  cycles: PerformanceCycle[];
  activeCycleId: string | null;
  cyclesLoading: boolean;

  // Definitions / Templates
  definitions: KPIDefinition[];
  templates: KPIDefinition[];
  definitionsLoading: boolean;
  templatesLoading: boolean;

  // Assignments
  myDashboard: DashboardMyKPIs | null;
  teamDashboard: DashboardTeam | null;
  orgDashboard: DashboardOrg | null;
  dashboardLoading: boolean;
  teamDashboardLoading: boolean;
  orgDashboardLoading: boolean;

  // Detail view
  detailCheckIns: KPICheckIn[];
  detailComments: KPIComment[];
  detailReviews: KPIReview[];
  detailLoading: boolean;

  // AI Chat
  aiMessages: Array<{ role: string; content: string }>;
  aiLoading: boolean;
  aiConversationId: string | null;
}

interface KPIActions {
  // Navigation
  setActiveView: (view: KPIView) => void;
  setSelectedAssignment: (id: string | null) => void;
  setShowAssignModal: (show: boolean) => void;
  setShowCheckInForm: (show: boolean) => void;

  // Cycles
  fetchCycles: () => Promise<void>;
  createCycle: (data: Parameters<typeof kpiApi.createCycle>[0]) => Promise<PerformanceCycle>;
  setActiveCycleId: (id: string | null) => void;
  updateCycleStatus: (cycleId: string, status: string) => Promise<void>;

  // Definitions
  fetchDefinitions: (category?: string) => Promise<void>;
  fetchTemplates: (category?: string) => Promise<void>;
  createDefinition: (data: Parameters<typeof kpiApi.createDefinition>[0]) => Promise<KPIDefinition>;

  // Assignments
  createAssignment: (data: Parameters<typeof kpiApi.createAssignment>[0]) => Promise<KPIAssignment>;
  approveAssignment: (id: string) => Promise<void>;
  deleteAssignment: (id: string) => Promise<void>;
  archiveAssignment: (id: string) => Promise<void>;

  // Delete
  deleteCycle: (id: string) => Promise<void>;
  deleteDefinition: (id: string) => Promise<void>;

  // Dashboards
  fetchMyDashboard: (cycleId?: string) => Promise<void>;
  fetchTeamDashboard: (cycleId?: string) => Promise<void>;
  fetchOrgDashboard: (cycleId?: string, departmentId?: string) => Promise<void>;

  // Check-ins
  submitCheckIn: (assignmentId: string, data: {
    value?: number; notes?: string; evidence_ids?: string[];
    financial_impact_value?: number;
  }) => Promise<void>;
  fetchCheckIns: (assignmentId: string) => Promise<void>;
  verifyCheckIn: (checkInId: string, status: string, notes?: string) => Promise<void>;
  uploadEvidence: (checkInId: string, file: File) => Promise<kpiApi.EvidenceUploadResponse>;

  // Comments
  addComment: (assignmentId: string, text: string) => Promise<void>;
  fetchComments: (assignmentId: string) => Promise<void>;

  // Reviews
  submitReview: (assignmentId: string, data: Parameters<typeof kpiApi.createReview>[1]) => Promise<void>;
  fetchReviews: (assignmentId: string) => Promise<void>;

  // AI
  sendAIMessage: (message: string, contextType?: string, contextId?: string) => Promise<void>;
  clearAIChat: () => void;

  // Detail (loads all detail data for an assignment)
  loadAssignmentDetail: (assignmentId: string) => Promise<void>;
}

export const useKPIStore = create<KPIState & KPIActions>((set, get) => ({
  // ── Initial State ──
  activeView: 'my-kpis',
  selectedAssignmentId: null,
  showAssignModal: false,
  showCheckInForm: false,

  cycles: [],
  activeCycleId: null,
  cyclesLoading: false,

  definitions: [],
  templates: [],
  definitionsLoading: false,
  templatesLoading: false,

  myDashboard: null,
  teamDashboard: null,
  orgDashboard: null,
  dashboardLoading: false,
  teamDashboardLoading: false,
  orgDashboardLoading: false,

  detailCheckIns: [],
  detailComments: [],
  detailReviews: [],
  detailLoading: false,

  aiMessages: [],
  aiLoading: false,
  aiConversationId: null,

  // ── Navigation ──
  setActiveView: (view) => set({ activeView: view }),
  setSelectedAssignment: (id) => set({ selectedAssignmentId: id }),
  setShowAssignModal: (show) => set({ showAssignModal: show }),
  setShowCheckInForm: (show) => set({ showCheckInForm: show }),

  // ── Cycles ──
  fetchCycles: () => {
    if (get().cycles.length > 0) return Promise.resolve();
    if (_kpiInflight['cycles']) return _kpiInflight['cycles'];
    _kpiInflight['cycles'] = (async () => {
      set({ cyclesLoading: true });
      try {
        const cycles = await kpiApi.listCycles();
        const activeCycle = cycles.find(c => c.status === 'active') || cycles[0] || null;
        set({ cycles, cyclesLoading: false, activeCycleId: activeCycle?.id || null });
      } catch {
        set({ cyclesLoading: false });
      } finally {
        delete _kpiInflight['cycles'];
      }
    })();
    return _kpiInflight['cycles'];
  },

  createCycle: async (data) => {
    const cycle = await kpiApi.createCycle(data);
    set(s => ({ cycles: [cycle, ...s.cycles] }));
    return cycle;
  },

  setActiveCycleId: (id) => set({ activeCycleId: id }),

  updateCycleStatus: async (cycleId, status) => {
    const updated = await kpiApi.updateCycle(cycleId, { status });
    set(s => ({
      cycles: s.cycles.map(c => c.id === cycleId ? updated : c),
    }));
  },

  // ── Definitions ──
  fetchDefinitions: (category) => {
    const key = `definitions:${category ?? ''}`;
    if (_kpiInflight[key]) return _kpiInflight[key];
    _kpiInflight[key] = (async () => {
      set({ definitionsLoading: true });
      try {
        const definitions = await kpiApi.listDefinitions(category);
        set({ definitions, definitionsLoading: false });
      } catch {
        set({ definitionsLoading: false });
      } finally {
        delete _kpiInflight[key];
      }
    })();
    return _kpiInflight[key];
  },

  fetchTemplates: (category) => {
    if (get().templates.length > 0) return Promise.resolve();
    if (_kpiInflight['templates']) return _kpiInflight['templates'];
    _kpiInflight['templates'] = (async () => {
      set({ templatesLoading: true });
      try {
        const templates = await kpiApi.listTemplates(category);
        set({ templates, templatesLoading: false });
      } catch {
        set({ templatesLoading: false });
      } finally {
        delete _kpiInflight['templates'];
      }
    })();
    return _kpiInflight['templates'];
  },

  createDefinition: async (data) => {
    const defn = await kpiApi.createDefinition(data);
    set(s => ({ definitions: [...s.definitions, defn] }));
    return defn;
  },

  // ── Assignments ──
  createAssignment: async (data) => {
    const assignment = await kpiApi.createAssignment(data);
    // Refresh dashboard
    const { fetchMyDashboard, activeCycleId } = get();
    if (activeCycleId) fetchMyDashboard(activeCycleId);
    return assignment;
  },

  approveAssignment: async (id) => {
    await kpiApi.approveAssignment(id);
    const { fetchMyDashboard, activeCycleId } = get();
    if (activeCycleId) fetchMyDashboard(activeCycleId);
  },

  deleteAssignment: async (id) => {
    await kpiApi.deleteAssignment(id);
    set({ selectedAssignmentId: null });
    const { fetchMyDashboard, fetchTeamDashboard, activeCycleId } = get();
    if (activeCycleId) {
      await Promise.all([fetchMyDashboard(activeCycleId), fetchTeamDashboard(activeCycleId)]);
    }
  },

  archiveAssignment: async (id) => {
    await kpiApi.archiveAssignment(id);
    set({ selectedAssignmentId: null });
    const { fetchMyDashboard, fetchTeamDashboard, activeCycleId } = get();
    if (activeCycleId) {
      await Promise.all([fetchMyDashboard(activeCycleId), fetchTeamDashboard(activeCycleId)]);
    }
  },

  deleteCycle: async (id) => {
    await kpiApi.deleteCycle(id);
    set(s => ({
      cycles: s.cycles.filter(c => c.id !== id),
      activeCycleId: s.activeCycleId === id ? (s.cycles.find(c => c.id !== id)?.id || null) : s.activeCycleId,
    }));
  },

  deleteDefinition: async (id) => {
    await kpiApi.deleteDefinition(id);
    set(s => ({ definitions: s.definitions.filter(d => d.id !== id) }));
  },

  // ── Dashboards ──
  fetchMyDashboard: (cycleId) => {
    const cid = cycleId || get().activeCycleId || undefined;
    const key = `myDash:${cid ?? ''}`;
    if (_kpiInflight[key]) return _kpiInflight[key];
    _kpiInflight[key] = (async () => {
      set({ dashboardLoading: true });
      try {
        const dashboard = await kpiApi.getMyDashboard(cid);
        set({ myDashboard: dashboard, dashboardLoading: false });
      } catch {
        set({ dashboardLoading: false });
      } finally {
        delete _kpiInflight[key];
      }
    })();
    return _kpiInflight[key];
  },

  fetchTeamDashboard: (cycleId) => {
    const cid = cycleId || get().activeCycleId || undefined;
    const key = `teamDash:${cid ?? ''}`;
    if (_kpiInflight[key]) return _kpiInflight[key];
    _kpiInflight[key] = (async () => {
      set({ teamDashboardLoading: true });
      try {
        const dashboard = await kpiApi.getTeamDashboard(cid);
        set({ teamDashboard: dashboard, teamDashboardLoading: false });
      } catch {
        set({ teamDashboardLoading: false });
      } finally {
        delete _kpiInflight[key];
      }
    })();
    return _kpiInflight[key];
  },

  fetchOrgDashboard: (cycleId, departmentId) => {
    const cid = cycleId || get().activeCycleId || undefined;
    const key = `orgDash:${cid ?? ''}:${departmentId ?? ''}`;
    if (_kpiInflight[key]) return _kpiInflight[key];
    _kpiInflight[key] = (async () => {
      set({ orgDashboardLoading: true });
      try {
        const dashboard = await kpiApi.getOrgDashboard(cid, departmentId);
        set({ orgDashboard: dashboard, orgDashboardLoading: false });
      } catch {
        set({ orgDashboardLoading: false });
      } finally {
        delete _kpiInflight[key];
      }
    })();
    return _kpiInflight[key];
  },

  // ── Check-ins ──
  submitCheckIn: async (assignmentId, data) => {
    await kpiApi.createCheckIn(assignmentId, data);
    // Refresh detail and dashboard
    const { loadAssignmentDetail, fetchMyDashboard, activeCycleId } = get();
    await loadAssignmentDetail(assignmentId);
    if (activeCycleId) fetchMyDashboard(activeCycleId);
  },

  fetchCheckIns: (assignmentId) => {
    const key = `checkIns:${assignmentId}`;
    if (_kpiInflight[key]) return _kpiInflight[key];
    _kpiInflight[key] = (async () => {
      try {
        const checkIns = await kpiApi.listCheckIns(assignmentId);
        set({ detailCheckIns: checkIns });
      } finally {
        delete _kpiInflight[key];
      }
    })();
    return _kpiInflight[key];
  },

  verifyCheckIn: async (checkInId, status, notes) => {
    const updated = await kpiApi.verifyCheckIn(checkInId, status, notes);
    set(s => ({
      detailCheckIns: s.detailCheckIns.map(ci => ci.id === checkInId ? updated : ci),
    }));
  },

  uploadEvidence: async (checkInId, file) => {
    return await kpiApi.uploadEvidence(checkInId, file);
  },

  // ── Comments ──
  addComment: async (assignmentId, text) => {
    const comment = await kpiApi.createComment(assignmentId, { text });
    set(s => ({ detailComments: [...s.detailComments, comment] }));
  },

  fetchComments: (assignmentId) => {
    const key = `comments:${assignmentId}`;
    if (_kpiInflight[key]) return _kpiInflight[key];
    _kpiInflight[key] = (async () => {
      try {
        const comments = await kpiApi.listComments(assignmentId);
        set({ detailComments: comments });
      } finally {
        delete _kpiInflight[key];
      }
    })();
    return _kpiInflight[key];
  },

  // ── Reviews ──
  submitReview: async (assignmentId, data) => {
    await kpiApi.createReview(assignmentId, data);
    const { fetchReviews } = get();
    await fetchReviews(assignmentId);
  },

  fetchReviews: (assignmentId) => {
    const key = `reviews:${assignmentId}`;
    if (_kpiInflight[key]) return _kpiInflight[key];
    _kpiInflight[key] = (async () => {
      try {
        const reviews = await kpiApi.listReviews(assignmentId);
        set({ detailReviews: reviews });
      } finally {
        delete _kpiInflight[key];
      }
    })();
    return _kpiInflight[key];
  },

  // ── AI ──
  sendAIMessage: async (message, contextType, contextId) => {
    set(s => ({
      aiMessages: [...s.aiMessages, { role: 'user', content: message }],
      aiLoading: true,
    }));
    try {
      const { reply, conversation_id } = await kpiApi.aiChat(message, contextType, contextId);
      set(s => ({
        aiMessages: [...s.aiMessages, { role: 'assistant', content: reply }],
        aiLoading: false,
        aiConversationId: conversation_id,
      }));
    } catch {
      set(s => ({
        aiMessages: [...s.aiMessages, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }],
        aiLoading: false,
      }));
    }
  },

  clearAIChat: () => set({ aiMessages: [], aiConversationId: null }),

  // ── Detail ──
  loadAssignmentDetail: (assignmentId) => {
    const key = `detail:${assignmentId}`;
    if (_kpiInflight[key]) return _kpiInflight[key];
    _kpiInflight[key] = (async () => {
      set({ detailLoading: true, selectedAssignmentId: assignmentId });
      try {
        const [checkIns, comments, reviews] = await Promise.all([
          kpiApi.listCheckIns(assignmentId),
          kpiApi.listComments(assignmentId),
          kpiApi.listReviews(assignmentId),
        ]);
        set({ detailCheckIns: checkIns, detailComments: comments, detailReviews: reviews, detailLoading: false });
      } catch {
        set({ detailLoading: false });
      } finally {
        delete _kpiInflight[key];
      }
    })();
    return _kpiInflight[key];
  },
}));
